import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/** In painting order — alphabetical by brand, the way every checkout footer lines them up. */
export const PAYMENT_METHODS = [
    {
        key: 'amex',
        fieldId: 'showAmex',
        label: 'American Express',
        defaultOn: true,
        aliases: ['american_express', 'americanexpress'],
    },
    {
        key: 'apple_pay',
        fieldId: 'showApplePay',
        label: 'Apple Pay',
        defaultOn: true,
        aliases: ['applepay'],
    },
    {
        key: 'bancontact',
        fieldId: 'showBancontact',
        label: 'Bancontact',
        defaultOn: false,
        aliases: [],
    },
    { key: 'bizum', fieldId: 'showBizum', label: 'Bizum', defaultOn: false, aliases: [] },
    {
        key: 'cod',
        fieldId: 'showCod',
        label: 'Cash on delivery',
        labelKey: 'checkout.pay_on_delivery',
        defaultOn: true,
        aliases: ['cash_on_delivery', 'cash'],
    },
    {
        key: 'diners_club',
        fieldId: 'showDinersClub',
        label: 'Diners Club',
        defaultOn: false,
        aliases: ['diners', 'dinersclub'],
    },
    { key: 'discover', fieldId: 'showDiscover', label: 'Discover', defaultOn: false, aliases: [] },
    {
        key: 'google_pay',
        fieldId: 'showGooglePay',
        label: 'Google Pay',
        defaultOn: true,
        aliases: ['googlepay'],
    },
    { key: 'maestro', fieldId: 'showMaestro', label: 'Maestro', defaultOn: false, aliases: [] },
    {
        key: 'mastercard',
        fieldId: 'showMastercard',
        label: 'Mastercard',
        defaultOn: true,
        aliases: ['master', 'master_card'],
    },
    { key: 'paypal', fieldId: 'showPaypal', label: 'PayPal', defaultOn: true, aliases: [] },
    {
        key: 'shop_pay',
        fieldId: 'showShopPay',
        label: 'Shop Pay',
        defaultOn: false,
        aliases: ['shopify_pay', 'shoppay'],
    },
    { key: 'visa', fieldId: 'showVisa', label: 'Visa', defaultOn: true, aliases: [] },
];
/** One switch per method, for a schema's `fields` — the block and the footer spread the same list. */
export function paymentMethodFields(group) {
    return PAYMENT_METHODS.map((m) => ({
        id: m.fieldId,
        type: 'checkbox',
        label: m.label,
        group,
        default: m.defaultOn,
    }));
}
/** A legacy free-text name («Apple-Pay», «google pay») → the catalog key, or null when nothing matches. */
function legacyKey(raw) {
    const name = raw
        .trim()
        .toLowerCase()
        .replace(/[\s-]+/g, '_');
    if (!name)
        return null;
    const hit = PAYMENT_METHODS.find((m) => m.key === name || m.aliases.includes(name));
    return hit ? hit.key : null;
}
/**
 * The methods switched on, in painting order.
 *
 * A switch that was TOUCHED wins. Untouched, the method falls back to the legacy free-text list when the
 * strip still carries one (so a strip configured before the switches existed paints exactly what it
 * painted), else to the catalog default.
 */
export function enabledPaymentMethods(settings) {
    const legacyRaw = typeof settings.enabledPaymentTypes === 'string' ? settings.enabledPaymentTypes : '';
    const legacy = legacyRaw
        .split(',')
        .map(legacyKey)
        .filter((k) => k !== null);
    const legacySet = legacyRaw.trim() ? new Set(legacy) : null;
    return PAYMENT_METHODS.filter((m) => {
        const v = settings[m.fieldId];
        if (typeof v === 'boolean')
            return v;
        if (legacySet)
            return legacySet.has(m.key);
        return m.defaultOn;
    });
}
// ── The drawings ─────────────────────────────────────────────────────────────────────────────────────
// Every card is 38×24 (the proportion of the real cards buyers know), radius 3. A brand that fills its
// card (Visa, Mastercard, American Express, Shop Pay) gets its colour edge to edge; the others sit on
// white behind a thin grey border so they read as cards on any footer colour.
const CARD_BORDER = '#d9d9d9';
function card(label, children, bg = '#ffffff', bordered = true) {
    return (_jsxs("svg", { viewBox: "0 0 38 24", role: "img", "aria-label": label, focusable: "false", style: { display: 'block' }, children: [_jsx("title", { children: label }), bordered ? (_jsx("rect", { x: "0.5", y: "0.5", width: "37", height: "23", rx: "3", fill: bg, stroke: CARD_BORDER })) : (_jsx("rect", { x: "0", y: "0", width: "38", height: "24", rx: "3", fill: bg })), children] }));
}
const SANS = 'Arial, Helvetica, sans-serif';
// Apple's mark (simple-icons, CC0) — the one glyph a text fallback cannot stand in for.
const APPLE_MARK = 'M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701';
// PayPal's monogram (simple-icons, CC0): the back «P» then the front «P», coloured separately below.
const PAYPAL_BACK = 'M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106z';
const PAYPAL_FRONT = 'M21.222 6.917a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.254-.93 4.778-4.005 7.201-9.138 7.201h-2.19a.563.563 0 0 0-.556.479l-1.187 7.527h-.506l-.24 1.516a.56.56 0 0 0 .554.647h3.882c.46 0 .85-.334.922-.788.06-.26.76-4.852.816-5.09a.932.932 0 0 1 .923-.788h.58c3.76 0 6.705-1.528 7.565-5.946.36-1.847.174-3.388-.777-4.471z';
/** The two overlapping discs shared by Mastercard and Maestro: left, right, and the lens where they meet. */
function discs(left, right, lens) {
    return (_jsxs(_Fragment, { children: [_jsx("circle", { cx: "14.5", cy: "12", r: "7", fill: left }), _jsx("circle", { cx: "23.5", cy: "12", r: "7", fill: right }), _jsx("path", { d: "M19 6.64A7 7 0 0 0 19 17.36A7 7 0 0 0 19 6.64Z", fill: lens })] }));
}
function draw(key, label) {
    switch (key) {
        case 'visa':
            return card(label, _jsx("text", { x: "19", y: "16.2", textAnchor: "middle", fontFamily: `'Arial Black', 'Arial Bold', ${SANS}`, fontWeight: "900", fontStyle: "italic", fontSize: "11", letterSpacing: "-0.3", fill: "#ffffff", children: "VISA" }), '#1434cb', false);
        case 'mastercard':
            return card(label, discs('#eb001b', '#f79e1b', '#ff5f00'), '#000000', false);
        case 'maestro':
            return card(label, discs('#0099df', '#ed0006', '#6c6bbd'));
        case 'amex':
            return card(label, _jsxs(_Fragment, { children: [_jsx("rect", { x: "7.5", y: "5.5", width: "23", height: "13", rx: "1.5", fill: "none", stroke: "#ffffff" }), _jsx("text", { x: "19", y: "15.4", textAnchor: "middle", fontFamily: SANS, fontWeight: "800", fontSize: "8", letterSpacing: "0.2", fill: "#ffffff", children: "AMEX" })] }), '#1f72cd', false);
        case 'shop_pay':
            return card(label, _jsxs("text", { x: "19", y: "15.8", textAnchor: "middle", fontFamily: SANS, fontSize: "9", fill: "#ffffff", children: [_jsx("tspan", { fontWeight: "800", children: "shop" }), _jsx("tspan", { fontWeight: "400", children: "Pay" })] }), '#5a31f4', false);
        case 'apple_pay':
            return card(label, _jsxs(_Fragment, { children: [_jsx("g", { transform: "translate(8 6.3) scale(0.46)", children: _jsx("path", { d: APPLE_MARK, fill: "#000000" }) }), _jsx("text", { x: "19", y: "16.3", fontFamily: `-apple-system, 'Helvetica Neue', ${SANS}`, fontWeight: "500", fontSize: "10", fill: "#000000", children: "Pay" })] }));
        case 'google_pay':
            return card(label, _jsxs(_Fragment, { children: [_jsxs("g", { fill: "none", strokeWidth: "2.3", children: [_jsx("path", { d: "M6.46 8.96A4.3 4.3 0 0 1 12.54 8.96", stroke: "#ea4335" }), _jsx("path", { d: "M6.46 15.04A4.3 4.3 0 0 1 6.46 8.96", stroke: "#fbbc04" }), _jsx("path", { d: "M12.54 15.04A4.3 4.3 0 0 1 6.46 15.04", stroke: "#34a853" }), _jsx("path", { d: "M13.8 12A4.3 4.3 0 0 1 12.54 15.04", stroke: "#4285f4" })] }), _jsx("rect", { x: "9.5", y: "10.85", width: "5.45", height: "2.3", fill: "#4285f4" }), _jsx("text", { x: "16.5", y: "16.3", fontFamily: `Roboto, ${SANS}`, fontWeight: "500", fontSize: "10", fill: "#5f6368", children: "Pay" })] }));
        case 'paypal':
            return card(label, _jsxs("g", { transform: "translate(12.6 5.4) scale(0.6)", children: [_jsx("path", { d: PAYPAL_BACK, fill: "#003087" }), _jsx("path", { d: PAYPAL_FRONT, fill: "#009cde" })] }));
        case 'discover':
            return card(label, _jsxs(_Fragment, { children: [_jsx("text", { x: "18", y: "14.2", textAnchor: "end", fontFamily: SANS, fontWeight: "700", fontSize: "6.4", fill: "#231f20", children: "DISC" }), _jsx("circle", { cx: "20.3", cy: "11.9", r: "2.3", fill: "#f58220" }), _jsx("text", { x: "22.8", y: "14.2", textAnchor: "start", fontFamily: SANS, fontWeight: "700", fontSize: "6.4", fill: "#231f20", children: "VER" })] }));
        case 'diners_club':
            return card(label, _jsxs(_Fragment, { children: [_jsx("circle", { cx: "19", cy: "12", r: "7.5", fill: "#0079be" }), _jsx("circle", { cx: "19", cy: "12", r: "5.2", fill: "#ffffff" }), _jsx("rect", { x: "14.6", y: "8.4", width: "2.6", height: "7.2", rx: "1.3", fill: "#0079be" }), _jsx("rect", { x: "20.8", y: "8.4", width: "2.6", height: "7.2", rx: "1.3", fill: "#0079be" })] }));
        case 'bancontact':
            return card(label, _jsxs(_Fragment, { children: [_jsx("path", { d: "M5 16 L11.5 8 H19 L12.5 16 Z", fill: "#005498" }), _jsx("path", { d: "M19 16 L25.5 8 H33 L26.5 16 Z", fill: "#ffd800" })] }));
        case 'bizum':
            return card(label, _jsx("text", { x: "19", y: "15.6", textAnchor: "middle", fontFamily: SANS, fontWeight: "700", fontSize: "9.5", letterSpacing: "-0.2", fill: "#1fb1ac", children: "bizum" }));
        case 'cod':
            return card(label, _jsxs("g", { fill: "none", stroke: "#2e7d32", strokeWidth: "1.4", children: [_jsx("rect", { x: "9.5", y: "6.5", width: "19", height: "11", rx: "1.5" }), _jsx("circle", { cx: "19", cy: "12", r: "2.6" }), _jsx("circle", { cx: "13", cy: "12", r: "0.9", fill: "#2e7d32", stroke: "none" }), _jsx("circle", { cx: "25", cy: "12", r: "0.9", fill: "#2e7d32", stroke: "none" })] }));
        default:
            return null;
    }
}
/** The 38×24 card for one method. `label` is what a screen reader announces and a hover shows. */
export function PaymentIcon({ method, label, }) {
    return draw(method.key, label);
}
//# sourceMappingURL=paymentIcons.js.map